import { Button, Layout, PageHeader, Popconfirm } from 'antd';
import { useContext, useState } from 'react';
import type { ReactNode } from 'react';

import { CurrencyContext } from 'app/components/commons/Currency/CurrencyContext/CurrencyContext';
import { CurrencyWrapper } from 'app/components/commons/Currency/CurrencyWrapper/CurrencyWrapper';
import { PictureComponent } from 'app/components/commons/PictureComponent/PictureComponent';
import { ProfilIcon } from 'app/components/commons/icons/DefaultProfile';
import { useUserBookings } from 'app/hooks/data/core/admin/users/useUserBookings';
import { useCountries } from 'app/hooks/data/useCountries';
import { useGetUser } from 'app/hooks/data/users/useGetUsers';
import { useDeleteUser, useSaveUser } from 'app/hooks/data/users/useUser';
import type { FormUser, UserDetail } from 'app/typings';
import { USER_ROLES } from 'app/typings';
import { BOOKING_STATUS } from 'app/typings/core/admin/bookings/enum/bookingStatus';
import { dateAndHour } from 'app/utils/dates';
import { format as formatDate } from 'app/utils/dayjs';
import { capitalizeFirstLetter } from 'app/utils/string';

import 'styles/pages/Users/user-details.scss';

import { BookingLinked } from './BookingsLinked';
import { GiftCardsLinked } from './GiftCardsLinked';
import { EditUserModal } from './Modals/EditUserModal';

const GIFT_CARD_PAYED_STATUSES = ['active', 'expired', 'empty', 'cancelled'];
const MAX_SHOWN_REFEREES = 7;

const UserDetailsContent = ({
  history,
  userDetails,
  userId,
}: UserDetailsContentProps) => {
  const { currencyFormatter } = useContext(CurrencyContext);

  const { mutate: saveUser } = useSaveUser();
  const { mutate: deleteUser } = useDeleteUser();
  const { data: userBookings, isLoading: isLoadingBookings } =
    useUserBookings(userId);
  const [showAllReferrees, setShowAllReferrees] = useState(false);

  const [editModalVisible, setEditModalVisible] = useState<boolean>(false);

  const handleDeleteUser = () => {
    deleteUser(userId);
  };

  const handleSaveUser = (data: FormUser) => {
    saveUser(data);
    setEditModalVisible(false);
  };

  const mean = (arr: number[]) =>
    !!arr.length ? arr.reduce((acc, num) => acc + num, 0) / arr.length : 0;

  const back = () => {
    history.goBack();
  };

  if (userDetails && userDetails.user.id === userId) {
    const line = (title: string, value: ReactNode, className = '') => (
      <div className="line">
        <span className="left-title">{title}</span>
        <span
          style={{ marginLeft: 20, textAlign: 'end' }}
          className={className}
        >
          {value}
        </span>
      </div>
    );

    const { referres, user, userGiftCards } = userDetails;
    const userAcceptedBookings = userBookings?.filter(
      (booking) => booking.status === BOOKING_STATUS.ACCEPTED
    );
    const { gender, firstName, lastName, id, deletedAt, referralCode } =
      userDetails?.user;

    const filteredReferrees = showAllReferrees
      ? referres
      : referres.slice(0, MAX_SHOWN_REFEREES);

    const getUserName = () => {
      if (!firstName || !lastName) {
        return `#${id}`;
      }

      return gender === undefined || gender === 'Other'
        ? `${firstName} ${lastName}`
        : `${gender} ${firstName} ${lastName}`;
    };

    const bookingsAmount = userAcceptedBookings?.reduce(
      (acc, obj) => (obj.totalAmount ? acc + Number(obj.totalAmount) : 0),
      0
    );

    const filteredGiftCards = userGiftCards.filter((giftCard) =>
      GIFT_CARD_PAYED_STATUSES.includes(giftCard.status)
    );

    const deleteConfirmTitle = 'User deletion is a definitive action';
    const User = (
      <div className="bloc">
        <div className="header-title-button">
          <div className="title-with-button">Profile</div>
          {!deletedAt && (
            <div className="delete-user">
              <Popconfirm
                title={deleteConfirmTitle}
                placement="bottomRight"
                onConfirm={handleDeleteUser}
                okText="Yes"
                cancelText="No"
              >
                <Button>Delete</Button>
              </Popconfirm>
            </div>
          )}
        </div>

        <div className="user-content">
          {user && user.avatarPath ? (
            <PictureComponent
              className="avatar"
              style={{ borderRadius: '50%' }}
              pictureId={user.avatarPath}
            />
          ) : (
            <ProfilIcon />
          )}
          <div className="details">
            <div className="name">{getUserName()}</div>
            {user && user.birthDate && (
              <div className="birth-date">
                {formatDate(new Date(user.birthDate), 'DD/MM/YYYY ')}
              </div>
            )}
          </div>
        </div>
        <div className="referral-code">
          {line('Referral code', <span>{referralCode}</span>)}
        </div>
      </div>
    );

    const Contact =
      user.email || user.phoneNumber ? (
        <div className="bloc">
          <div className="title">Contact</div>
          <div className="generic-content">
            {user.email && line('E-mail', user.email, 'e-mail')}
            {user.phoneNumber && line('Tel', user.phoneNumber)}
          </div>
        </div>
      ) : undefined;

    const Settings = (
      <div className="bloc">
        <div className="title">Settings</div>
        <div className="generic-content">
          {line('Club', user.club)}
          {line('Locality', user.locality)}
          {line('Language', user.langage)}
        </div>
      </div>
    );

    const Logs = (
      <div className="small bloc">
        <div className="title">Logs</div>
        <div className="generic-content">
          {line('ID', user.id)}
          {line('Role', capitalizeFirstLetter(user.role))}
          {line('Created at', dateAndHour(user.createdAt))}
          {user.lastLogin && line('Last log-in', dateAndHour(user.lastLogin))}
        </div>
      </div>
    );

    const bookingsAverage =
      userAcceptedBookings && bookingsAmount
        ? bookingsAmount / userAcceptedBookings.length
        : 0;

    const scoresMeans = userBookings?.flatMap((booking) => {
      const globalScore = booking.globalScore;

      if (typeof globalScore !== 'number' || !isFinite(globalScore)) {
        return [];
      }

      return [globalScore];
    }, []);

    const ratingMean = scoresMeans ? mean(scoresMeans) : 0;

    // eslint-disable-next-line @typescript-eslint/no-shadow -- inner-scope reuse, not a bug
    const getLink = (id: number) => (
      <a
        href={`/users/${`${id}`}/edit`}
        style={{ color: '#2D2D2D', textDecoration: 'underline' }}
      >
        {id}
      </a>
    );

    const Activity = (
      <div className="small bloc">
        <div className="title">Activity</div>
        <div className="generic-content">
          {line('Staycations', userAcceptedBookings?.length)}
          {ratingMean > 0 && line('Average rating', `${ratingMean.toFixed(2)}`)}
          {userBookings !== undefined && userBookings?.length > 0
            ? line('Average amount', currencyFormatter(bookingsAverage))
            : null}
          {line('Total spending', currencyFormatter(bookingsAmount))}
          {user.referrerId !== undefined &&
            line('Referrer', getLink(user.referrerId))}
          {line(
            'Referrees',
            <span style={{ fontWeight: 500 }}>{referres.length}</span>
          )}
          {filteredReferrees.map((refereeId) => (
            <div className="line" style={{ marginTop: 0 }} key={`${refereeId}`}>
              <span className="left-title" />
              <span style={{ marginLeft: 20, textAlign: 'end' }}>
                {getLink(refereeId)}
              </span>
            </div>
          ))}
          {referres.length > MAX_SHOWN_REFEREES && (
            <div className="line" style={{ marginTop: 10 }}>
              <span className="left-title" />
              <Button onClick={() => setShowAllReferrees(!showAllReferrees)}>
                {showAllReferrees ? 'Show less' : 'View more'}
              </Button>
            </div>
          )}
        </div>
      </div>
    );

    const renderEdit = (
      <div className="user-edit-btn">
        <Button size="large" onClick={() => setEditModalVisible(true)}>
          Edit
        </Button>
      </div>
    );

    return (
      <Layout className="user-details">
        <PageHeader
          className="header"
          onBack={back}
          title={`User ${user.id}`}
          extra={!deletedAt && renderEdit}
        />
        <div className="body">
          <div className="left-part">
            {User}
            {Contact}
            {Settings}
          </div>
          <div className="right-part">
            <div className="row">
              {Logs}
              {Activity}
            </div>
            <BookingLinked
              bookings={userBookings}
              isLoading={isLoadingBookings}
            />
            <GiftCardsLinked giftCards={filteredGiftCards} />
          </div>
        </div>
        <EditUserModal
          visible={editModalVisible}
          onSave={handleSaveUser}
          onCancel={() => setEditModalVisible(false)}
          roles={USER_ROLES}
          userId={userId}
        />
      </Layout>
    );
  }

  return null;
};

export const UserDetails = ({ history, match }: Props) => {
  const userId = parseInt(match.params?.id ?? '', 10);

  const { data: userDetails } = useGetUser(userId);
  const { data: countries } = useCountries();

  const countrySlug = userDetails?.user?.countrySlug || '';
  const country = countries?.find((c) => c.slug === countrySlug);

  return (
    <CurrencyWrapper countryId={country?.id}>
      <UserDetailsContent
        userDetails={userDetails}
        history={{ goBack: history.goBack }}
        userId={userId}
      />
    </CurrencyWrapper>
  );
};

type Props = {
  history: { goBack: () => void };
  match: {
    params: {
      id?: string;
    };
  };
};
type UserDetailsContentProps = {
  history: { goBack: () => void };
  userDetails?: UserDetail;
  userId: number;
};
